import React, { useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import api from '../utils/api'; // Assicurati che il percorso importi correttamente la tua istanza axios

const ChatbotContact = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });

    const BRAND_CORAL = "#E84C5B";

    const toggleOpen = () => setIsOpen(!isOpen);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        try {
            // Utilizziamo lo stesso endpoint usato nella pagina ContactContent
            const res = await api.post('/content/contact', formData);
            if (res.data.success) {
                setStatus('success');
                setFormData({ name: '', email: '', message: '' });
                // Chiude automaticamente dopo 3 secondi di successo
                setTimeout(() => {
                    setStatus('idle');
                    setIsOpen(false);
                }, 3000);
            } else {
                setStatus('error');
            }
        } catch (error) {
            console.error("Error sending message", error);
            setStatus('error');
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end font-sans">

            {/* --- FINESTRA DELLA CHAT --- */}
            {isOpen && (
                <div className="mb-4 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in-up origin-bottom-right transition-all transform duration-300">

                    {/* Header Chat */}
                    <div className="bg-[#1F2A33] p-4 flex justify-between items-center text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                                <MessageCircle className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm">Contattaci</h3>
                                <p className="text-[10px] text-gray-300 opacity-80">Rispondiamo solitamente in 24h</p>
                            </div>
                        </div>
                        <button onClick={toggleOpen} className="text-gray-400 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Body Chat / Form */}
                    <div className="p-5 bg-gray-50">
                        {status === 'success' ? (
                            <div className="text-center py-8 animate-fade-in">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <Send className="w-6 h-6 text-green-600" />
                                </div>
                                <p className="font-bold text-green-700">Messaggio Inviato!</p>
                                <p className="text-xs text-gray-500 mt-1">Grazie per averci contattato.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Il tuo nome"
                                        required
                                        className="w-full p-3 rounded-xl border border-gray-200 text-sm focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="La tua email"
                                        required
                                        className="w-full p-3 rounded-xl border border-gray-200 text-sm focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Come possiamo aiutarti?"
                                        rows={3}
                                        required
                                        className="w-full p-3 rounded-xl border border-gray-200 text-sm focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none resize-none transition-all"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === 'sending'}
                                    style={{ backgroundColor: BRAND_CORAL }}
                                    className="w-full text-white py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-md"
                                >
                                    {status === 'sending' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            Invia Messaggio <Send className="w-3 h-3" />
                                        </>
                                    )}
                                </button>
                                {status === 'error' && (
                                    <p className="text-xs text-red-500 text-center font-bold">Errore nell'invio. Riprova più tardi.</p>
                                )}
                            </form>
                        )}
                    </div>

                    {/* Footer powered by (opzionale) */}
                    <div className="bg-gray-100 p-2 text-center border-t border-gray-200">
                        <span className="text-[10px] text-gray-400 font-medium">Powered by Another Home</span>
                    </div>
                </div>
            )}

            {/* --- PULSANTE FLOATING --- */}
            <button
                onClick={toggleOpen}
                style={{ backgroundColor: isOpen ? '#1F2A33' : BRAND_CORAL }}
                className="w-14 h-14 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center text-white relative group"
            >
                {isOpen ? (
                    <X className="w-6 h-6 transform rotate-90 animate-in spin-in duration-300" />
                ) : (
                    <>
                        <MessageCircle className="w-7 h-7" />
                        {/* Ping Animation per attirare attenzione */}
                        <span className="absolute top-0 right-0 -mt-1 -mr-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-400"></span>
                        </span>
                    </>
                )}
            </button>
        </div>
    );
};

export default ChatbotContact;